import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { Box, Button, Callout, Flex, Heading, Separator, Text } from '@radix-ui/themes'
import { AlertCircle, Info } from 'lucide-react'
import Form from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'
import type { IChangeEvent } from '@rjsf/core'
import { radixTemplates, radixWidgets } from '@/shared/credentials/rjsf/RadixTheme'
import { CredentialsNewAnswers } from '@/types/credentials_new'
import { CredentialsEditProps } from '@/types/credentials_edit'

export default function CredentialsEdit({
  credential_form,
  schema,
  ui_schema,
  answers,
  rejection_reason,
  event_name,
  submit_path,
  cancel_path,
  errors,
}: CredentialsEditProps) {
  const [formData, setFormData] = useState<CredentialsNewAnswers>(answers)
  const [processing, setProcessing] = useState(false)

  // RJSF owns the form state, so submit off its `formData` rather than
  // Inertia's useForm — the two would race on every keystroke.
  const handleSubmit = ({ formData }: IChangeEvent<CredentialsNewAnswers>) => {
    router.patch(
      submit_path,
      { answers: formData ?? {} },
      { onStart: () => setProcessing(true), onFinish: () => setProcessing(false) }
    )
  }

  return (
    <>
      <Head title={`Fix your ${credential_form.title}`} />
      <Box style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <Box className="border-2 py-8 px-10">
          <Heading size="7">{credential_form.title}</Heading>
          <Text size="4" as="p" color="gray">
            {credential_form.track_name} sent this back. Change what needs changing and submit it again —
            everything you answered before is still here.
            {event_name && ` Then you can finish your application for ${event_name}.`}
          </Text>
          <Separator my="3" size="4" />

          <Callout.Root color="amber" variant="soft" size="2" my="3">
            <Callout.Icon>
              <Info size={18} />
            </Callout.Icon>
            <Callout.Text>
              {rejection_reason ? (
                <>
                  <Text weight="bold">{credential_form.track_name} said:</Text> {rejection_reason}
                </>
              ) : (
                <>
                  {credential_form.track_name} didn't say what needs changing. Reach out to them if you're not
                  sure.
                </>
              )}
            </Callout.Text>
          </Callout.Root>

          {errors && errors.length > 0 && (
            <Callout.Root color="red" role="alert" my="3">
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>
                {errors.map((error, i) => (
                  <Box key={i}>{error}</Box>
                ))}
              </Callout.Text>
            </Callout.Root>
          )}

          <Form
            schema={schema}
            uiSchema={ui_schema}
            widgets={radixWidgets}
            templates={radixTemplates}
            formData={formData}
            validator={validator}
            onChange={({ formData }) => setFormData(formData ?? {})}
            onSubmit={handleSubmit}
          >
            <Box mt="8">
              <Flex gap="4" justify="end">
                <Button type="button" size="3" asChild color="gray" variant="soft">
                  <Link href={cancel_path}>Cancel</Link>
                </Button>
                <Button size="3" type="submit" disabled={processing}>
                  Send it back for review
                </Button>
              </Flex>
            </Box>
          </Form>
        </Box>
      </Box>
    </>
  )
}
